import { useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Typography,
  Card,
  Space,
  Tooltip,
  message,
  InputNumber,
  Tag,
  Input,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
const { Text } = Typography;

const StickersSection = ({ stickerState, updateStickerState }) => {
  const { prefix, startRange, endRange, manual, stickers } = stickerState;

  const handleGenerateStickers = () => {
    if (
      !startRange ||
      !endRange ||
      isNaN(startRange) ||
      isNaN(endRange) ||
      Number(startRange) < 0 ||
      Number(endRange) < 0
    ) {
      message.error("Please enter valid sticker numbers");
      return;
    }
    if (String(startRange).length > 5 || String(endRange).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }
    const start = parseInt(startRange, 10);
    const end = parseInt(endRange, 10);
    if (start > end) {
      message.error("Start should be less than end");
      return;
    }
    if (end - start > 99) {
      message.error("Maximum 100 Stickers can be generated at a time");
      return;
    }
    const newStickers = [];
    for (let i = start; i <= end; i++) {
      const numericPart = String(i).padStart(5, "0");
      const sticker = prefix.trim() ? `${prefix}-${numericPart}` : numericPart;
      if (!stickers.includes(sticker)) {
        newStickers.push(sticker);
      }
    }
    if (newStickers?.length === 0) {
      message.info("No new unique stickers to add");
      return;
    }
    updateStickerState({ stickers: [...stickers, ...newStickers] });
  };

  const handleAddManualSticker = () => {
    if (!manual || isNaN(manual) || Number(manual) < 0) {
      message.error("Please enter valid sticker numbers");
      return;
    }
    if (String(manual).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }
    const numericPart = String(manual).padStart(5, "0");
    const newSticker = prefix.trim() ? `${prefix}-${numericPart}` : numericPart;
    if (stickers.includes(newSticker)) {
      message.warning("Sticker No. already exists");
      return;
    }
    updateStickerState({ stickers: [...stickers, newSticker], manual: "" });
  };

  const handleDeleteSticker = (sticker) => {
    updateStickerState({ stickers: stickers.filter((s) => s !== sticker) });
  };

  // Handle prefix changes when stickers already exist
  useEffect(() => {
    if (stickers.length > 0) {
      const updatedStickers = stickers.map((sticker) => {
        const parts = sticker.split("-");
        const numericPart = parts[parts.length - 1];
        return prefix.trim() ? `${prefix}-${numericPart}` : numericPart;
      });
      updateStickerState({ stickers: updatedStickers });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);

  return (
    <Card
      style={{
        borderRadius: 12,
        border: "1px solid #E5E8EC",
        background: "#fff",
        marginBottom: 16,
      }}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{
          borderBottom: "2px solid #23235F",
          marginBottom: 16,
          paddingBottom: 12,
        }}
      >
        <Text style={{ fontSize: 18, fontWeight: 700, color: "#23235F" }}>
          🏷️ Stickers
        </Text>
        <Space align="center">
          <Text style={{ fontWeight: 600, color: "#232A36", fontSize: 13 }}>
            Prefix
          </Text>
          <Input
            value={prefix}
            onChange={(e) => updateStickerState({ prefix: e.target.value })}
            placeholder="e.g., AG-ST"
            style={{ borderRadius: 8, fontWeight: 600, width: 120 }}
          />
        </Space>
      </Row>

      {/* Sticker Range */}
      <Text style={{ fontWeight: 600, color: "#232A36" }}>Sticker Range</Text>
      <Row gutter={8} style={{ marginTop: 6, marginBottom: 16 }}>
        <Col span={9}>
          <InputNumber
            value={startRange}
            onChange={(value) => updateStickerState({ startRange: value })}
            placeholder="Start"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={9}>
          <InputNumber
            value={endRange}
            onChange={(value) => updateStickerState({ endRange: value })}
            placeholder="End"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Button
            type="primary"
            onClick={handleGenerateStickers}
            style={{
              width: "100%",
              borderRadius: 8,
              fontWeight: 700,
              background: "#23235F",
            }}
          >
            Generate Stickers
          </Button>
        </Col>
      </Row>

      {/* Add Manual Sticker */}
      <Text style={{ fontWeight: 600, color: "#232A36" }}>
        Add Manual Sticker
      </Text>
      <Row gutter={12} style={{ marginTop: 6, marginBottom: 15 }}>
        <Col span={18}>
          <InputNumber
            value={manual}
            onChange={(value) => updateStickerState({ manual: value })}
            placeholder="Enter Numeric Value (e.g., 1)"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Button
            type="primary"
            onClick={handleAddManualSticker}
            style={{
              width: "100%",
              borderRadius: 8,
              fontWeight: 700,
              background: "#23235F",
            }}
          >
            Add Sticker
          </Button>
        </Col>
      </Row>

      {/* Sticker IDs */}
      {stickers?.length ? (
        <Card
          style={{
            borderRadius: 15,
            border: "1px solid #E5E8EC",
            background: "#FAFAFA",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Text style={{ fontWeight: 700, fontSize: 16, color: "#232A36" }}>
              Sticker IDs
            </Text>
            <Tag
              style={{
                fontWeight: 700,
                fontSize: 15,
                background: "#E5E8EC",
                borderRadius: 6,
                padding: "4px 12px",
                border: "none",
              }}
            >
              {stickers.length} {`Sticker${stickers.length > 1 ? "s" : ""}`}
            </Tag>
          </Row>
          <Row
            gutter={[12, 12]}
            style={{
              border: "1px solid #E5E8EC",
              padding: "12px",
              borderRadius: 12,
              overflow: "auto",
              maxHeight: 220,
            }}
          >
            {stickers.map((sticker, idx) => (
              <Col span={8} key={sticker + idx}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#fff",
                    borderRadius: 8,
                    border: "1px solid #E5E8EC",
                    padding: "4px 12px",
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#232A36",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span>{sticker}</span>
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSticker(sticker)}
                      style={{ color: "#E14D4F", fontSize: 16 }}
                    />
                  </Tooltip>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      ) : null}
    </Card>
  );
};

export default StickersSection;
